<script setup lang="ts" generic="T">
import { ref, computed, watch } from 'vue'

const modelValue = defineModel<T>()

const emit = defineEmits(['update:modelValue', 'focusChange'])
const { label = 'Label',
  icon,
  disabled = false,
  isValid = true,
  errorMessage = 'Eingabe ist nicht korrekt',
  focusColor = 'blue',
  charLength,
  minWidth = '200px',
  type = 'text',
  min = null,
  max = null,
  step = 'any' } = defineProps<{
  label: string
  icon?: string
  disabled?: boolean
  isValid?: boolean
  errorMessage?: string
  focusColor?: 'green' | 'blue' | 'orange' | 'yellow'
  charLength?: number
  minWidth?: string
  type?: string
  min?: number | null
  max?: number | null
  step?: number | 'any'
}>()

const inputElement = ref<HTMLInputElement | null>(null)
const isFocused = ref(false)

const onFocus = () => {
  isFocused.value = true
  emit('focusChange', true)
}
const onBlur = () => {
  isFocused.value = false
  emit('focusChange', false)
}

defineExpose({
  focus: () => inputElement.value?.focus(),
  select: () => inputElement.value?.select()
})

const focusColorValue = computed(() => {
  if (!isValid) {
    return '#C42216'
  }

  switch (focusColor) {
      case 'green':
        return '#A1B46F'
      case 'blue':
        return '#0974F2'
      case 'orange':
        return '#f7941d'
      case 'yellow':
        return '#ECAA10'
      default:
        return '#747474'
  }
})

watch(modelValue, (newValue) => {
  emit('update:modelValue', newValue)
})
</script>

<template>
  <div class="w-full">
    <div v-if="isValid === true" :class="`h-10 min-w-[${minWidth}] relative w-full`" :style="{ '--focus-border-color': focusColorValue }">
      <input
        ref="inputElement"
        v-model="modelValue"
        :type="type"
        :disabled="disabled"
        :maxlength="charLength"
        :min="min"
        :max="max"
        :step="step"
        class="input-default peer"
        :data-1p-ignore="type !== 'password'"
        placeholder=" "
        data-cy="input-element"
        @focus="onFocus"
        @blur="onBlur"
      >
      <div v-if="icon" class="absolute right-2 bottom-2">
        <Icon v-if="icon === 'feather:alert-circle'" class="text-[30px] text-metras-red" :icon="icon" />
        <Icon v-else class="text-[24px] text-metras-grey" :icon="icon" />
      </div>
      <label
        class="custom-input-label
            label-default
            peer-disabled:after:border-metras-middle-grey
            peer-disabled:before:border-metras-middle-grey
            peer-disabled:peer-placeholder-shown:after:border-transparent
            peer-disabled:peer-placeholder-shown:before:border-transparent
            peer-focus:after:border-[var(--focus-border-color)]
            peer-focus:after:border-r-2
            peer-focus:after:border-t-2
            peer-focus:before:border-[var(--focus-border-color)]
            peer-focus:before:border-l-2
            peer-focus:before:border-t-2
            peer-focus:leading-tight
            peer-focus:text-[11px]
            peer-focus:text-[var(--focus-border-color)]
            peer-placeholder-shown:after:border-transparent
            peer-placeholder-shown:before:border-transparent
            peer-placeholder-shown:leading-[3.75]
            peer-placeholder-shown:text-metras-grey
            peer-placeholder-shown:text-sm"
      >
        {{ label }}
      </label>
    </div>
    <div v-else :class="`h-10 min-w-[${minWidth}] relative w-full`">
      <input
        v-model="modelValue"
        :type="type"
        :disabled="disabled"
        :maxlength="charLength"
        :min="min"
        :max="max"
        :step="step"
        class="input-error peer"
        :data-1p-ignore="type !== 'password'"
        placeholder=" "
        data-cy="input-element"
      >
      <div v-if="icon" class="absolute right-2 bottom-2">
        <Icon class="text-[24px] text-metras-red" icon="feather:alert-circle" />
      </div>
      <label
        class="label-error
            peer-disabled:after:border-metras-middle-grey
            peer-disabled:before:border-metras-middle-grey
            peer-focus:after:border-metras-red
            peer-focus:after:border-r-2
            peer-focus:after:border-t-2
            peer-focus:before:border-l-2
            peer-focus:before:border-metras-red
            peer-focus:before:border-t-2
            peer-focus:leading-tight
            peer-focus:text-[11px]
            peer-focus:text-metras-red
            peer-placeholder-shown:after:border-transparent
            peer-placeholder-shown:before:border-transparent
            peer-placeholder-shown:leading-[3.75]
            peer-placeholder-shown:text-metras-red
            peer-placeholder-shown:text-sm"
      >
        {{ label }}
      </label>
      <p v-if="isValid" class="pl-3 pt-1 text-metras-red text-xs">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
.input-default {
  @apply h-full w-full rounded border
  border-metras-grey
  border-t-transparent
  bg-transparent
  px-3
  py-2.5
  font-sans
  text-sm
  font-normal
  text-black
  outline
  outline-0
  transition-all
  placeholder-shown:border
  placeholder-shown:border-metras-grey
  placeholder-shown:border-t-metras-grey
  focus:border-2
  focus:border-[var(--focus-border-color)]
  focus:border-t-transparent
  focus:outline-0
  disabled:border-metras-middle-grey
  disabled:border-t-transparent
  disabled:placeholder-shown:border-t-metras-middle-grey
  disabled:bg-transparent;
}

.label-default {
  @apply pointer-events-none absolute left-0 -top-1.5 truncate flex h-full w-full select-none text-[11px] font-normal leading-tight transition-all text-metras-grey;
  &:before {
    content: ' ';
    @apply pointer-events-none mt-[6.5px] mr-1 box-border block h-1.5 w-2.5 rounded-tl border-t border-l transition-all border-metras-grey;
  }

  &:after {
    content: ' ';
    @apply pointer-events-none mt-[6.5px] ml-1 box-border block h-1.5 w-2.5 flex-grow rounded-tr border-t border-r transition-all border-metras-grey;
  }
}

.input-error {
  @apply h-full w-full rounded border
  border-metras-red
  border-t-transparent
  bg-transparent
  px-3
  py-2.5
  font-sans
  text-sm
  font-normal
  text-black
  outline
  outline-0
  transition-all
  placeholder-shown:border
  placeholder-shown:border-metras-red
  placeholder-shown:border-t-metras-red
  focus:border-2
  focus:border-metras-red
  focus:border-t-transparent
  focus:outline-0
  disabled:border-metras-red
  disabled:bg-transparent;
}

.label-error {
  @apply pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-normal leading-tight transition-all text-metras-red;
  &:before {
    content: ' ';
    @apply pointer-events-none mt-[6.5px] mr-1 box-border block h-1.5 w-2.5 rounded-tl border-t border-l transition-all border-metras-red;
  }

  &:after {
    content: ' ';
    @apply pointer-events-none mt-[6.5px] ml-1 box-border block h-1.5 w-2.5 flex-grow rounded-tr border-t border-r transition-all border-metras-red;
  }
}
</style>
